import { formatDateReadable } from "utils/formatDate"
import {
  IAttendanceRecord,
  IMetricEntry,
  IShiftSwapRequest,
} from "./interface/shiftData"

export const startOfWeek = (date: Date): Date => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  return new Date(d.setDate(diff))
}

export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export const formatDate = (
  date: Date,
  options: Intl.DateTimeFormatOptions
): string => new Intl.DateTimeFormat("en-US", options).format(date)

export const toKeyDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export const formatDay = (dateStr: string) =>
  dateStr
    ? formatDate(new Date(dateStr), { month: "short", day: "2-digit" })
    : ""

export const formatDateLong = (dateStr: string) =>
  formatDate(new Date(dateStr), {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })

export const formatDateNumeric = (date: string | null) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : ""

export const formatTime = (dateStr: string | null | undefined) => {
  if (!dateStr) return "pending"
  const timePart = dateStr.split("T")[1]
  if (!timePart) return "pending"
  const [h, m, s] = timePart.split(":")
  const sec = s ? s.substring(0, 2) : "00"
  return `${h}:${m}:${sec}`
}

export const calculateDistanceInMeters = (
  employeeLatitude: number,
  employeeLongitude: number,
  locationLatitude: number,
  locationLongitude: number
): number => {
  const earthRadiusInMeters = 6371000
  const latitudeDifference =
    ((locationLatitude - employeeLatitude) * Math.PI) / 180
  const longitudeDifference =
    ((locationLongitude - employeeLongitude) * Math.PI) / 180
  const haversineA =
    Math.sin(latitudeDifference / 2) * Math.sin(latitudeDifference / 2) +
    Math.cos((employeeLatitude * Math.PI) / 180) *
      Math.cos((locationLatitude * Math.PI) / 180) *
      Math.sin(longitudeDifference / 2) *
      Math.sin(longitudeDifference / 2)
  const haversineC =
    2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA))
  return earthRadiusInMeters * haversineC
}

export const getCurrentTime = () => {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}`
}

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number)
  return h * 60 + m
}

const formatDuration = (minutes: number): string => {
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60)
    return `${hrs} hr${hrs > 1 ? "s" : ""}`
  }
  return `${minutes} min${minutes !== 1 ? "s" : ""}`
}

export const getClockStatus = (
  shiftTime: string,
  type: "clock-in" | "clock-out"
): { status: string; duration: string } => {
  const now = new Date()
  const currentTotalMinutes = now.getHours() * 60 + now.getMinutes()
  const shiftTotalMinutes = toMinutes(shiftTime)
  const differenceInMinutes = Math.abs(currentTotalMinutes - shiftTotalMinutes)

  let status: string
  if (type === "clock-in") {
    status = currentTotalMinutes <= shiftTotalMinutes ? "Early" : "Late"
  } else {
    if (currentTotalMinutes < shiftTotalMinutes) status = "Early"
    else if (currentTotalMinutes === shiftTotalMinutes) status = "Ok"
    else status = "AfterShift"
  }

  return { status, duration: formatDuration(differenceInMinutes) }
}

export const calculateOvertimeDuration = (
  startTime: string,
  endTime: string
): string => {
  if (!startTime || !endTime) return "-"

  let diffMinutes = toMinutes(endTime) - toMinutes(startTime)
  if (diffMinutes < 0) diffMinutes += 24 * 60

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60

  const hourLabel = hours === 1 ? "hr" : "hrs"
  const minuteLabel = minutes === 1 ? "min" : "mins"

  if (hours === 0) return `${minutes}${minuteLabel}`
  if (minutes === 0) return `${hours}${hourLabel}`

  return `${hours}${hourLabel} : ${minutes}${minuteLabel}`
}

export const convertTo24Hr = (time: string) => {
  if (!time) return ""
  const [timePart, period] = time.split(" ")
  if (!timePart || !period) return time
  let [hrs, mins] = timePart.split(":").map(Number)
  if (period === "PM" && hrs !== 12) hrs += 12
  if (period === "AM" && hrs === 12) hrs = 0
  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}`
}

export const convertTo12Hr = (time: string): string => {
  if (!time) return ""
  const [hours, minutes] = time.split(":").map(Number)
  const period = hours >= 12 ? "PM" : "AM"
  const displayHours = hours % 12 || 12
  return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )} ${period}`
}

export const validateTimeInput = (value: string): string | null => {
  const [timePart] = (value || "").split(" ")
  const [h, m] = (timePart ?? "").split(":")
  const hours = Number(h)
  const minutes = Number(m)
  if (!h || !m || isNaN(hours) || isNaN(minutes))
    return "Please enter a valid time"
  if (h.length !== 2 || m.length !== 2)
    return "Time must use 2 digits (e.g., 03:00)"
  if (hours < 1 || hours > 12) return "Hours must be between 1 and 12"
  if (minutes < 0 || minutes > 59) return "Minutes must be between 0 and 59"
  return null
}

export const formatGroupLabel = (
  key: string,
  groupBy: "day" | "week" | "month"
): string => {
  if (groupBy === "day") return formatDateReadable(key)

  if (groupBy === "week") {
    const start = new Date(key)
    const end = addDays(start, 6)
    const fmt = (d: Date) => formatDate(d, { month: "short", day: "2-digit" })
    return `${fmt(start)} - ${fmt(end)} | ${end.getFullYear()}`
  }

  if (groupBy === "month") {
    const [year, month] = key.split("-")
    return formatDate(new Date(Number(year), Number(month) - 1, 1), {
      month: "long",
      year: "numeric",
    })
  }

  return key
}

const GREEN_BG = "#DCFCE7"
const GREEN_TEXT = "#008236"
const RED_BG = "#FEF2F2"
const RED_TEXT = "#E7000B"

export const formatDelta = (metric: IMetricEntry | undefined): string | null =>
  `${(metric?.delta ?? 0).toFixed(0)}%`

export const getBadgeColors = (
  metric: IMetricEntry | undefined,
  isPositiveMetric: boolean
): { badgeColor: string | null; badgeBgColor: string | null } => {
  const delta = metric?.delta ?? 0
  const isGood = isPositiveMetric ? delta >= 0 : delta <= 0
  return {
    badgeColor: isGood ? GREEN_TEXT : RED_TEXT,
    badgeBgColor: isGood ? GREEN_BG : RED_BG,
  }
}

export const formatSentenceCase = (text: string | undefined | null): string => {
  if (!text) return ""
  const lower = text.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export const getFlags = (record: IAttendanceRecord) => {
  const rules: {
    key: keyof IAttendanceRecord
    label: string
    check?: (val: any) => boolean
  }[] = [
    { key: "lateClockin", label: "Late Arrival" },
    { key: "earlyClockin", label: "Early Arrival" },
    {
      key: "geofenceStatus",
      label: "Outside Geofence",
      check: (val) => val === "outside" && !!record.shiftPlan?.allowGeoFencing,
    },
    { key: "origin", label: "Manual Entry", check: (val) => val === "manual" },
    { key: "earlyClockout", label: "Early Departure" },
    { key: "lateClockout", label: "Late Departure" },
    { key: "overtime", label: "Overtime" },
  ]

  return rules
    .filter((rule) =>
      rule.check ? rule.check(record[rule.key]) : record[rule.key]
    )
    .map((rule) => rule.label)
}

export const sortByDate = <T>(
  arr: T[],
  key: keyof T,
  order: "asc" | "desc" = "desc"
) => {
  return [...arr].sort((a, b) => {
    const aVal = String(a[key])
    const bVal = String(b[key])
    return order === "desc"
      ? bVal.localeCompare(aVal)
      : aVal.localeCompare(bVal)
  })
}

type SwapPillInfo = {
  text: string
  icon: "clock" | "check" | "cancel"
  bgColor: string
  textColor: string
  borderColor: string
}

export function getSwapPillInfo(
  swap: IShiftSwapRequest,
  perspective: "sender" | "receiver"
): SwapPillInfo | undefined {
  const requiresAdminApproval =
    swap.sourceShiftAssignment.shiftPlan?.requireApprovalForSwap

  if (swap.status === "pending") {
    const text = !swap.targetEmployeeApproved
      ? perspective === "sender"
        ? "Pending colleague's approval"
        : "Pending your approval"
      : "Pending admin's approval"
    return {
      text,
      icon: "clock",
      bgColor: "#F1F5F9",
      textColor: "#0F172B",
      borderColor: "#0F172B",
    }
  }

  if (swap.status === "approved") {
    const text = requiresAdminApproval
      ? perspective === "sender"
        ? "Approved by colleague & admin"
        : "Approved by you & admin"
      : perspective === "sender"
      ? "Approved by colleague"
      : "Approved by you"
    return {
      text,
      icon: "check",
      bgColor: "#F0FDF4",
      textColor: "#00A63E",
      borderColor: "#00A63E",
    }
  }

  if (swap.status === "rejected") {
    const text =
      swap.reviewedBy === swap.targetEmployee.email
        ? perspective === "sender"
          ? "Rejected by colleague"
          : "Rejected by you"
        : "Rejected by admin"
    return {
      text,
      icon: "cancel",
      bgColor: "#FEF2F2",
      textColor: "#E7000B",
      borderColor: "#E7000B",
    }
  }
}
